import { Head, Link } from "@inertiajs/react"
import { Building2, Shield, UserRound, Users } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Stats {
  organizations: number
  users: number
  memberships: number
  partners: number
}
interface ProductCount {
  product: string
  count: number
}
interface OrgCard {
  id: number
  name: string
  slug: string
  partner: boolean
  created_at: string
}
interface UserCard {
  id: number
  name: string
  email: string
  role: string
  created_at: string
}

interface Props {
  stats: Stats
  products: ProductCount[]
  recent_organizations: OrgCard[]
  recent_users: UserCard[]
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Admin Console", href: "/admin/console" },
]

function fmtDate(value: string) {
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

export default function AdminConsole({
  stats,
  products,
  recent_organizations,
  recent_users,
}: Props) {
  const cards = [
    { label: "Organizations", value: stats.organizations, icon: Building2 },
    { label: "Users", value: stats.users, icon: Users },
    { label: "Memberships", value: stats.memberships, icon: UserRound },
    { label: "Partner orgs", value: stats.partners, icon: Shield },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Admin Console" />
      <div className="space-y-6 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">Admin Console</h1>
            <p className="text-muted-foreground text-sm">
              Superadmin — manage organizations, users, and feature access
              across every tenant.
            </p>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline">
              <Link href="/admin/organizations">Organizations</Link>
            </Button>
            <Button asChild variant="outline">
              <Link href="/admin/users">Users</Link>
            </Button>
            <Button asChild variant="outline">
              <Link href="/admin/feature_flags">Feature Flags</Link>
            </Button>
            <Button asChild variant="outline">
              <Link href="/admin/quickbooks_connections">QuickBooks</Link>
            </Button>
            <Button asChild variant="outline">
              <Link href="/admin/email_archive">Email Archive</Link>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {cards.map((c) => (
            <Card key={c.label}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-muted-foreground text-sm font-medium">
                  {c.label}
                </CardTitle>
                <c.icon className="text-muted-foreground size-4" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-semibold">{c.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {products.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Orgs by product</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {products.map((p) => (
                <Badge key={p.product} variant="secondary">
                  {p.product} · {p.count}
                </Badge>
              ))}
            </CardContent>
          </Card>
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <div>
                <CardTitle className="text-base">
                  Recent organizations
                </CardTitle>
                <CardDescription>Newest tenants</CardDescription>
              </div>
              <Button asChild size="sm" variant="ghost">
                <Link href="/admin/organizations">View all</Link>
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {recent_organizations.map((o) => (
                <div
                  key={o.id}
                  className="flex items-center justify-between gap-2 text-sm"
                >
                  <span className="min-w-0 truncate">
                    <span className="font-medium">{o.name}</span>{" "}
                    <span className="text-muted-foreground">/{o.slug}</span>
                    {o.partner && (
                      <Badge variant="outline" className="ml-2">
                        partner
                      </Badge>
                    )}
                  </span>
                  <span className="text-muted-foreground shrink-0">
                    {fmtDate(o.created_at)}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <div>
                <CardTitle className="text-base">Recent users</CardTitle>
                <CardDescription>Newest sign-ups</CardDescription>
              </div>
              <Button asChild size="sm" variant="ghost">
                <Link href="/admin/users">View all</Link>
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {recent_users.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center justify-between gap-2 text-sm"
                >
                  <span className="min-w-0 truncate">
                    <span className="font-medium">{u.email}</span>
                    {u.role !== "member" && (
                      <Badge variant="outline" className="ml-2">
                        {u.role}
                      </Badge>
                    )}
                  </span>
                  <span className="text-muted-foreground shrink-0">
                    {fmtDate(u.created_at)}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  )
}
